import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { EventFactsLedgerRow } from '../factsApi';
import { fmtInt, fmtUsd, initials, unaccountedOf, type LedgerTotals } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';
import { SEGMENT_COLORS, SEGMENT_INK, SEGMENT_KEYS, TRACK_COLOR, type SegmentKey } from './chartTokens';
import { tileOf } from './PremiumsDelivered';

export function segmentsOf(row: Pick<EventFactsLedgerRow, 'allocated' | 'inPlay' | 'returned' | 'won' | 'delivered' | 'pending' | 'expired' | 'voided'>) {
  return {
    delivered: row.delivered,
    pending: row.pending,
    lapsed: row.expired + row.voided,
    returned: row.returned,
    unaccounted: unaccountedOf(row),
  } satisfies Record<SegmentKey, number>;
}

/**
 * El libro de premios, artículo a artículo: la suma es el argumento, así que
 * cada fila es una barra apilada cuyos segmentos suman literalmente lo que
 * salió del juego sobre lo asignado. Lo que queda sin color sigue en juego.
 */
export function LedgerBars({
  rows,
  totals,
  title,
  kicker,
  basis,
  showValue = true,
  className,
}: {
  rows: EventFactsLedgerRow[];
  totals: LedgerTotals;
  title?: string;
  kicker?: string;
  basis?: string;
  showValue?: boolean;
  className?: string;
}) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const totalSegs = segmentsOf(totals);
  return (
    <section ref={ref} className={cn('flex flex-col gap-[18px] rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      {(title || kicker || basis) && (
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="flex flex-col gap-[7px]">
            {kicker && <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-olive)' }}>{kicker}</span>}
            {title && <h3 className="text-[26px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{title}</h3>}
          </div>
          {basis && <span className="text-[13px]" style={{ color: 'var(--color-dash-fg-muted)' }}>{basis}</span>}
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-[12.5px]" style={{ color: 'var(--color-dash-fg-2)' }}>
        {SEGMENT_KEYS.map((k) => (
          <span key={k} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: SEGMENT_COLORS[k] }} />
            {t(`dash.seg.${k}`)}
            <span className="dash-mono font-bold" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(totalSegs[k])}</span>
          </span>
        ))}
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-[3px] border" style={{ background: TRACK_COLOR, borderColor: 'var(--color-dash-border)' }} />
          {t('dash.seg.inPlay')}
          <span className="dash-mono font-bold" style={{ color: 'var(--color-dash-fg)' }}>{fmtInt(totals.inPlay)}</span>
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {rows.map((row, i) => {
          const tile = tileOf(i);
          return (
            <div key={row.stockId} className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-[11px]">
                {row.image ? (
                  <img src={resolveImageUrl(row.image)} alt="" className="h-[34px] w-[34px] shrink-0 rounded-[9px] object-cover" />
                ) : (
                  <span className="dash-mono flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] text-xs font-bold" style={{ background: tile.bg, color: tile.fg }}>{initials(row.name)}</span>
                )}
                <span className="min-w-0 flex-1 basis-[140px] text-[15px] font-semibold" style={{ color: 'var(--color-dash-fg)' }}>{row.name}</span>
                <span className="dash-mono text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('dash.ledger.allocated', { n: fmtInt(row.allocated) })}</span>
                {showValue && (
                  <span className="dash-mono text-xs font-bold" style={{ color: row.distributedValue == null ? 'var(--color-dash-orange)' : 'var(--color-dash-fg)' }}>
                    {row.distributedValue == null ? t('dash.ledger.valuePending') : fmtUsd(row.distributedValue)}
                  </span>
                )}
              </div>
              <StackedBar segs={segmentsOf(row)} allocated={row.allocated} p={p} height={34} />
            </div>
          );
        })}

        {rows.length > 1 && (
          <div className="flex flex-col gap-2 border-t pt-4" style={{ borderColor: 'var(--color-dash-border)' }}>
            <div className="flex flex-wrap items-center gap-[11px]">
              <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.09em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('dash.ledger.total')}</span>
              <span className="flex-1" />
              <span className="dash-mono text-xs" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('dash.ledger.allocated', { n: fmtInt(totals.allocated) })}</span>
              {showValue && (
                <span className="dash-mono text-xs font-bold" style={{ color: totals.distributedValue.state === 'PENDING' ? 'var(--color-dash-orange)' : 'var(--color-dash-fg)' }}>
                  {totals.distributedValue.state === 'PENDING' ? t('dash.ledger.valuePending') : fmtUsd(totals.distributedValue.value)}
                </span>
              )}
            </div>
            <StackedBar segs={totalSegs} allocated={totals.allocated} p={p} height={42} />
          </div>
        )}
      </div>
    </section>
  );
}

/** Barra apilada sobre lo asignado; los segmentos crecen con la rampa. */
export function StackedBar({ segs, allocated, p = 1, height = 34 }: { segs: Record<SegmentKey, number>; allocated: number; p?: number; height?: number }) {
  const t = useT();
  const total = Math.max(allocated, SEGMENT_KEYS.reduce((a, k) => a + segs[k], 0)) || 1;
  return (
    <div className="flex w-full overflow-hidden rounded-lg" style={{ height, background: TRACK_COLOR }}>
      {SEGMENT_KEYS.map((k) => {
        const v = segs[k];
        if (!v) return null;
        const pct = (v / total) * 100;
        return (
          <div
            key={k}
            className="flex items-center justify-center overflow-hidden transition-[width] duration-[650ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]"
            style={{ width: `${(pct * p).toFixed(2)}%`, background: SEGMENT_COLORS[k] }}
            title={`${t(`dash.seg.${k}`)} · ${fmtInt(v)} · ${pct.toFixed(1)}%`}
          >
            <span className="dash-mono whitespace-nowrap text-[11px] font-bold" style={{ color: SEGMENT_INK[k] }}>{pct >= 7 ? fmtInt(v) : ''}</span>
          </div>
        );
      })}
    </div>
  );
}
